import { useEffect, useId, useRef, useState } from 'react'
import { BookingEmbed } from '@/components/audition/booking-embed'
import {
  StarterCheckout,
  type StarterCommitment,
} from '@/components/audition/starter-checkout'
import { useStageWatch } from '@/components/audition/use-stage-watch'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  type ArtefactStep,
  EMPTY_LANE_ANSWERS,
  LANE_TITLE,
  type Lane,
  type LaneAnswers,
  parseVideoLink,
  reachableStep,
  type StepIndex,
  videoSkippable,
} from '@/lib/audition/lane-steps'
import { formatPrice } from '@/lib/stripe/format-price'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

/** What the status line says after a Try again on the booking lookup. */
const RETRY_STATUS = {
  ready: 'The booking calendar is ready below.',
  unavailable: "Checked again — booking isn't set up here yet.",
  failed:
    "Still couldn't load the booking calendar — please try again in a moment.",
} as const

/**
 * The status line for a finished Try again — but only while the query still
 * shows the very result that retry produced. `retry` is the `updatedAt` of
 * that result. A background refetch (window focus, reconnect) outlives the
 * retry: once it settles the stamps move on and the line goes quiet, so it
 * neither keeps saying "couldn't load" above a calendar that arrived on its
 * own, nor announces "ready" again for a refetch the applicant never asked
 * for. While the retry itself is in flight the line is blank, so a second
 * identical failure is a change the live region announces.
 */
function retryStatus(
  embed: {
    data: unknown
    isError: boolean
    isSuccess: boolean
    dataUpdatedAt: number
    errorUpdatedAt: number
  },
  retry: 'pending' | number | null,
): string {
  if (retry === null || retry === 'pending') return ''
  if (Math.max(embed.dataUpdatedAt, embed.errorUpdatedAt) !== retry) return ''
  // Data first: a failed refetch over a cached calendar still renders the
  // calendar, so what the applicant sees is "ready".
  if (embed.data) return RETRY_STATUS.ready
  if (embed.isError) return RETRY_STATUS.failed
  if (embed.isSuccess) return RETRY_STATUS.unavailable
  return ''
}

/** The status line once a slot is taken, while the booking is confirmed. */
function bookedStatus(watch: { reached: boolean; stalled: boolean }): string {
  if (watch.reached) return "You're booked 🎉 — the invite is in your inbox."
  if (watch.stalled) {
    return 'Your booking is in — the confirmation is taking a moment to reach us, but the invite will arrive by email.'
  }
  return 'Confirming your booking…'
}

/** The starter pack as `audition.listPacks` reports it, when it has one. */
export interface StarterPrice {
  unitAmount: number
  currency: string
}

/**
 * Why a step's header refuses to open. Carried rather than collapsed to a
 * boolean because the two reasons read nothing alike to a screen reader:
 * "finish the step above" is a lie on step one of an application that is
 * already paid for. "Not locked" is `StepLock | null` at each use site, so
 * every one of them has to say what it means by the absence.
 */
type StepLock = 'unreached' | 'paid' | 'booked'

const LOCK_HINT: Record<StepLock, string> = {
  unreached: 'Finish the step above first',
  paid: 'Locked — your deposit is confirmed',
  booked: 'Locked — your call is booked',
}

/**
 * The three-step checkout an application lane ends in (LG-1033): video,
 * CV, then payment or a booking — the same accordion on both lanes, so the
 * thing that commits the applicant is always last.
 *
 * Which steps are finished and how far the accordion may open is decided
 * by `reachableStep`, not by which buttons happen to be drawn: a step past the
 * reach is disabled, so the paid lane cannot show the card form without a
 * recording no matter what the markup offers.
 *
 * Everything that survives a "Change" lives here rather than inside a step:
 * the panels unmount when they close, so a Checkout Session, a payment and a
 * booking held in a child's state would all be lost by reopening step 1. Once
 * the applicant has actually committed — paid or booked — the artefact steps
 * lock: you cannot un-pay by clicking Change.
 *
 * Recording (LG-424) and upload (LG-918) are not built yet; their controls
 * are drawn inert with the fallback each step has today (paste a link,
 * skip) so the flow is real end to end before they land.
 */
export function LaneSteps({
  lane,
  token,
  starter,
  onBack,
}: {
  lane: Lane
  token: string
  starter: StarterPrice | null
  onBack: () => void
}) {
  const [answers, setAnswers] = useState<LaneAnswers>(EMPTY_LANE_ANSWERS)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  // The half-typed link, kept here so a Change round-trip does not empty the
  // field the applicant already pasted into.
  const [videoLink, setVideoLink] = useState('')
  const [wanted, setWanted] = useState<StepIndex>(0)
  // The lane's commitments, lifted out of the panels that render them.
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paid, setPaid] = useState(false)
  const [booked, setBooked] = useState(false)
  const committed = paid || booked
  const reach = reachableStep(lane, answers)
  // A finished step may be reopened via Change; an unreached one never, and
  // none of them once the applicant has paid or booked.
  const open: StepIndex = committed ? 2 : wanted < reach ? wanted : reach

  // Advancing unmounts the panel holding the Continue/Skip button that was
  // just clicked, and focus falls to `<body>`: a keyboard or screen-reader
  // user is dropped at the top of the document with no word that the next
  // step opened. Move focus onto that step's header, the same repair the
  // booking step makes when its embed comes down. Only `advance` arms it —
  // clicking a header already put focus where it belongs, and an arriving
  // payment or booking moves focus to its own status line.
  const headers = useRef<(HTMLButtonElement | null)[]>([])
  const advanced = useRef(false)
  useEffect(() => {
    if (!advanced.current) return
    advanced.current = false
    headers.current[open]?.focus()
  })

  const advance = (next: LaneAnswers) => {
    advanced.current = true
    setAnswers(next)
    setWanted(reachableStep(lane, next))
  }

  // The artefact steps: locked by the commitment first, since after a payment
  // "finish the step above first" would be the wrong reason as well as a
  // false one.
  const lockFor = (step: ArtefactStep): StepLock | null =>
    paid ? 'paid' : booked ? 'booked' : reach >= step ? null : 'unreached'

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">{LANE_TITLE[lane]}</h1>
        <p className="text-muted-foreground">
          {lane === 'hold'
            ? 'Last page.'
            : 'Last page. Nothing to pay now, and nothing after until you say so.'}
        </p>
      </div>

      <ol className="space-y-3">
        <Step
          index={0}
          title="Record a short video"
          pill={videoSkippable(lane) ? 'Optional' : 'Required'}
          summary={
            answers.video === 'attached'
              ? (videoUrl ?? 'Recording attached')
              : answers.video === 'skipped'
                ? 'Skipped'
                : null
          }
          open={open === 0}
          lock={lockFor(0)}
          onOpen={() => setWanted(0)}
          headerRef={(el) => {
            headers.current[0] = el
          }}
        >
          <VideoStep
            lane={lane}
            link={videoLink}
            onLinkChange={setVideoLink}
            onAttach={(url) => {
              setVideoUrl(url)
              advance({ ...answers, video: 'attached' })
            }}
            onSkip={
              videoSkippable(lane)
                ? () => advance({ ...answers, video: 'skipped' })
                : null
            }
          />
        </Step>

        <Step
          index={1}
          title="Attach your CV"
          pill="Optional"
          summary={answers.cv === 'skipped' ? 'Skipped' : null}
          open={open === 1}
          lock={lockFor(1)}
          onOpen={() => setWanted(1)}
          headerRef={(el) => {
            headers.current[1] = el
          }}
        >
          <CvStep onSkip={() => advance({ ...answers, cv: 'skipped' })} />
        </Step>

        <Step
          index={2}
          title={lane === 'hold' ? 'Hold your spot' : 'Pick a time'}
          pill={null}
          summary={null}
          open={open === 2}
          lock={reach >= 2 ? null : 'unreached'}
          onOpen={() => setWanted(2)}
          headerRef={(el) => {
            headers.current[2] = el
          }}
        >
          {lane === 'hold' ? (
            <HoldStep
              token={token}
              starter={starter}
              commitment={{
                clientSecret,
                // `setClientSecret` by identity, not a fresh closure: it is a
                // dependency of the effect that hands the minted Session up.
                onClientSecret: setClientSecret,
                paid,
                onPaid: () => setPaid(true),
              }}
            />
          ) : (
            <BookStep
              token={token}
              booked={booked}
              onBooked={() => setBooked(true)}
            />
          )}
        </Step>
      </ol>

      <aside className="text-muted-foreground space-y-2 text-sm">
        {lane === 'hold' ? (
          <>
            <p>
              {starter
                ? `The ${formatPrice(starter.unitAmount, starter.currency)} deposit holds a place. It stays fully refundable until we decide.`
                : 'The deposit holds a place. It stays fully refundable until we decide.'}
            </p>
            <p>
              If we think your application is a good fit, it becomes your first
              month's payment. If we do not, it comes back in full, straight
              away.
            </p>
            <p>
              You can still book a call afterwards. We work with a small number
              of clients each month, on a first come, first served basis.
            </p>
          </>
        ) : (
          <>
            <p>
              Mike Mroczka or a member of his team will have reviewed your
              application before the call.
            </p>
            <p>
              This call is free, and a mutual chance for both of us to decide
              whether working together makes sense.
            </p>
            <p>
              No place is held while you wait. Anyone who chose "Hold your spot"
              moves ahead of you in the queue, so a busy month can fill before
              your slot comes round.
            </p>
          </>
        )}
      </aside>

      {/*
        Back unmounts this whole accordion — `LaneChoice` drops the lane and
        with it the payment, the booking and the Session held above. Once the
        applicant has committed there is nothing to go back to, so it goes
        away on the same condition Change does.
      */}
      {!committed && (
        <Button type="button" variant="link" className="px-0" onClick={onBack}>
          ← Back
        </Button>
      )}
    </section>
  )
}

/**
 * One accordion card. The header is a real button so it is keyboard- and
 * screen-reader-reachable. Past the reach it is `aria-disabled` rather than
 * `disabled`: it stays in the tab order, announces why it cannot be opened,
 * and the click handler refuses — so an unreached step is still unopenable
 * by any route, without silently vanishing for keyboard users.
 */
function Step({
  index,
  title,
  pill,
  summary,
  open,
  lock,
  onOpen,
  headerRef,
  children,
}: {
  index: StepIndex
  title: string
  pill: string | null
  summary: string | null
  open: boolean
  lock: StepLock | null
  onOpen: () => void
  /** So the list can move focus here when a step advances. */
  headerRef: (el: HTMLButtonElement | null) => void
  children: React.ReactNode
}) {
  const panelId = useId()
  const hintId = useId()
  const done = summary !== null
  const reachable = lock === null
  return (
    <li
      className={cn(
        'rounded-md border',
        open ? 'border-primary' : 'border-border',
        // Only an unreached step is dimmed. A step locked by the commitment
        // is finished, not unavailable, and greying it out says the opposite.
        lock === 'unreached' && 'opacity-60',
      )}
    >
      <button
        type="button"
        ref={headerRef}
        className="flex w-full items-center gap-3 px-4 py-3 text-left aria-disabled:cursor-not-allowed"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        // Only the locked state is announced as unavailable. The step the
        // applicant is working in is open, not disabled, and saying both
        // reads as "expanded, unavailable" with no reason offered.
        aria-disabled={!reachable}
        aria-describedby={lock === null ? undefined : hintId}
        onClick={() => {
          // An open header is a no-op rather than a collapse: there is always
          // exactly one open step, and it needs no ARIA of its own.
          if (!reachable || open) return
          onOpen()
        }}
      >
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
            done
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground',
          )}
          aria-hidden
        >
          {done ? '✓' : index + 1}
        </span>
        <span className="flex-1">
          <span className="font-medium">{title}</span>
          {pill && (
            <span className="text-muted-foreground ml-2 text-xs">{pill}</span>
          )}
          {done && !open && (
            <span className="text-muted-foreground block truncate text-sm">
              {summary}
            </span>
          )}
        </span>
        {done && !open && reachable && (
          <span className="text-primary text-sm underline">Change</span>
        )}
      </button>
      {lock !== null && (
        <span id={hintId} className="sr-only">
          {LOCK_HINT[lock]}
        </span>
      )}
      {open && (
        <div id={panelId} className="border-t px-4 py-4">
          {children}
        </div>
      )}
    </li>
  )
}

function VideoStep({
  lane,
  link,
  onLinkChange,
  onAttach,
  onSkip,
}: {
  lane: Lane
  /** Owned by `LaneSteps` so it outlives this panel's unmount. */
  link: string
  onLinkChange: (value: string) => void
  onAttach: (url: string) => void
  /** Null on the paid lane: no skip is offered, and `reachableStep` would refuse one. */
  onSkip: (() => void) | null
}) {
  const parsed = parseVideoLink(link)
  const linkId = useId()
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        {lane === 'hold'
          ? 'Two minutes is plenty, and unpolished is genuinely fine. A phone camera in a kitchen beats a script. Recording happens right here, so you never leave this page.'
          : 'Not required before a call, but the people who record one get more out of theirs, because we watch it beforehand. Two minutes, phone camera, no script.'}
      </p>
      <div className="space-y-1">
        {/* Inert until LG-424 lands the recorder. */}
        <Button type="button" disabled>
          Record here
        </Button>
        <p className="text-muted-foreground text-xs">
          Recording here isn't ready yet — paste a link instead.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor={linkId}>
          Or paste a link to a video you already have
        </Label>
        <Input
          id={linkId}
          type="url"
          inputMode="url"
          placeholder="https://"
          value={link}
          onChange={(e) => onLinkChange(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          disabled={!parsed}
          onClick={() => parsed && onAttach(parsed)}
        >
          Continue
        </Button>
        {onSkip && (
          <Button type="button" variant="ghost" onClick={onSkip}>
            Skip for now
          </Button>
        )}
      </div>
    </div>
  )
}

function CvStep({ onSkip }: { onSkip: () => void }) {
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        A CV helps us place your experience. PDF is ideal; anything readable
        works.
      </p>
      <div className="space-y-1">
        {/* Inert until LG-918 lands the upload. */}
        <Button type="button" variant="outline" disabled>
          Choose a file
        </Button>
        <p className="text-muted-foreground text-xs">
          Uploads aren't ready yet — skip for now and we'll ask again later.
        </p>
      </div>
      <Button type="button" variant="ghost" onClick={onSkip}>
        Skip for now
      </Button>
    </div>
  )
}

function HoldStep({
  token,
  starter,
  commitment,
}: {
  token: string
  starter: StarterPrice | null
  /** Owned by `LaneSteps`: a Session and a payment must outlive this panel. */
  commitment: StarterCommitment
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm">
        {starter
          ? `${formatPrice(starter.unitAmount, starter.currency)} today — fully refundable until we decide. If we think your application is a good fit, it becomes your first month's payment.`
          : "Fully refundable until we decide. If we think your application is a good fit, it becomes your first month's payment."}
      </p>
      <StarterCheckout
        address={{ token }}
        submitLabel="Hold my spot"
        commitment={commitment}
      />
      <p className="text-muted-foreground text-xs">
        Secured by Stripe. If you're not accepted, the deposit comes back in
        full.
      </p>
    </div>
  )
}

function BookStep({
  token,
  booked,
  onBooked,
}: {
  token: string
  /** Owned by `LaneSteps`: a booking must outlive this panel. */
  booked: boolean
  onBooked: () => void
}) {
  // The fast lane stays token-only on purpose, so `LaneSteps` and its step
  // components keep `token: string` props while the rest of /apply moved to
  // `SubmissionAddress` (LG-1056): the lane is entered from the account step,
  // where a token exists by construction, and the whole lane sits behind
  // `enable-fast-lane`, which is off. Threading an address through here would
  // widen an unreachable surface. Re-decide that when the flag ships.
  const embed = trpc.audition.bookingEmbed.useQuery({ token })
  const watch = useStageWatch({
    address: { token },
    active: booked,
    until: ['vibe_call_booked'],
  })
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  // Which fetch the last Try again settled on, for the status line below:
  // 'pending' while it runs, then the `updatedAt` of its result (see
  // `retryStatus`). A retry that works unmounts the button under the
  // focused element, and a retry that fails re-renders the alert with the
  // same words — which no screen reader repeats. Either way the
  // always-mounted status line says what happened.
  const [retry, setRetry] = useState<'pending' | number | null>(null)

  // One live region, in the tree from the first render with only its text
  // changing: a `role="status"` created together with its content is
  // announced inconsistently across screen readers, and this line is the
  // booking's only confirmation.
  const status = booked ? bookedStatus(watch) : retryStatus(embed, retry)

  // The embed comes down the moment a slot is taken. Left live behind the
  // confirming line — up to ninety seconds, longer if the webhook is late —
  // the applicant can navigate back inside the iframe and book a second slot
  // on the same event type.
  const target = booked ? null : embed.data

  // Taking the embed away takes the focused element with it, and focus falls
  // to `<body>`: a keyboard or screen-reader user is dropped at the top of
  // the document with no word that the booking worked. Move focus onto the
  // line that says so. `booked` only ever goes false→true within a mount, so
  // this fires once, at the moment the iframe leaves.
  const statusRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (booked) statusRef.current?.focus()
  }, [booked])

  return (
    <div className="space-y-3">
      <p ref={statusRef} tabIndex={-1} role="status" className="text-sm">
        {status}
      </p>
      {target && (
        <>
          <div className="min-h-[560px]">
            <BookingEmbed target={target} onBooked={onBooked} />
          </div>
          <p className="text-muted-foreground text-xs">
            {`Times are shown in your timezone (${timeZone}).`}
          </p>
        </>
      )}
      {/* A failed lookup is not an unconfigured one. Rate limiting, an
          outage or a dropped connection all leave `data` undefined, and
          telling that applicant "not set up here yet" sends them away from
          a step that would work on a retry.

          Gated on there being nothing to show, not on `isError` alone: a
          refetch that fails — the window-focus one after half a minute
          away, say — flips the status to error but keeps the cached
          target, so the iframe above stays up and working. An alert under
          a working calendar announces a failure that never happened. */}
      {!booked && !embed.data && embed.isError && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">
            We couldn't load the booking calendar — please try again.
          </p>
          <Button
            type="button"
            variant="outline"
            // `aria-disabled` rather than `disabled`, as the step headers do:
            // a native `disabled` blurs the focused button the moment the
            // refetch starts, and the failure branch below relies on the
            // button still holding focus. The handler refuses the work
            // instead.
            className="aria-disabled:pointer-events-none aria-disabled:opacity-50"
            aria-disabled={embed.isFetching}
            onClick={() => {
              if (embed.isFetching) return
              setRetry('pending')
              void embed.refetch().then((result) => {
                setRetry(Math.max(result.dataUpdatedAt, result.errorUpdatedAt))
                // A retry that works unmounts this button under the focused
                // element and focus falls to `<body>`: the applicant is
                // dropped at the top of the page with no sign the retry did
                // anything. Land focus on the status line instead, the same
                // repair the booking uses above, so they hear the outcome
                // and are still inside step 3 when the calendar appears. On
                // a failure the button is still here and keeps focus.
                if (!result.isError) statusRef.current?.focus()
              })
            }}
          >
            {embed.isFetching ? 'Trying…' : 'Try again'}
          </Button>
        </div>
      )}
      {!booked && embed.isSuccess && !embed.data && (
        <p className="text-sm">
          Booking isn't set up here yet — we'll email you a few times to pick
          from.
        </p>
      )}
    </div>
  )
}
